import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useCart from "../context/useCart";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../services/firebase";

export default function Checkout() {
  const { cart, total, clearCart } = useCart();
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  // 👉 Mets l’URL de ta function déployée ici (ou via variable d’env)
  const CREATE_PAY_URL =
    window.__PAYGATE_FN__ ||
    "https://europe-west1-ghana-67644.cloudfunctions.net/createPaygatePayment";

  const fmt = useMemo(
    () => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "XOF" }),
    []
  );

  const handleChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const validateForm = () => {
    if (!form.name.trim() || !form.address.trim() || !form.phone.trim()) {
      return "Veuillez compléter tous les champs (nom, téléphone, adresse).";
    }
    if (!cart || cart.length === 0) return "Le panier est vide.";
    return "";
  };

  async function createOrderDoc({ paymentMethod, status, extra = {} }) {
    const order = {
      customer: {
        name: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
      },
      items: cart.map((i) => ({
        id: i.id,
        name: i.name,
        price: Number(i.price) || 0,
        qty: i.qty || 0,
      })),
      total: Number(total) || 0,
      currency: "XOF",
      paymentMethod, // 'whatsapp' | 'paygate' | 'cod'
      status,        // 'pending_whatsapp' | 'awaiting_payment' | 'pending'
      createdAt: serverTimestamp(),
      ...extra,
    };
    const ref = await addDoc(collection(db, "ordersWine"), order);
    return ref.id;
  }

  const handleWhatsApp = async () => {
    setError("");
    const v = validateForm();
    if (v) return setError(v);

    setLoading(true);
    try {
      const orderId = await createOrderDoc({
        paymentMethod: "whatsapp",
        status: "pending_whatsapp",
      });

      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const lines = cart
        .map(
          (i) =>
            `• ${i.name} × ${i.qty} = ${fmt.format((Number(i.price) || 0) * (i.qty || 0))}`
        )
        .join("\n");
      const msg = encodeURIComponent(
        `Bonjour 👋, je souhaite valider ma commande #${orderId} :\n\n` +
          `${lines}\n\n` +
          `Total: ${fmt.format(total)}\n\n` +
          `Nom: ${form.name}\nTel: ${form.phone}\nAdresse: ${form.address}\n\n` +
          `Suivi: ${origin}/orders`
      );
      const phone = "393895513136"; // sans '+'
      window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");

      clearCart();
      nav("/orders");
    } catch (e) {
      console.error(e);
      setError("Impossible de créer la commande (WhatsApp). Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const handleCOD = async () => {
    setError("");
    const v = validateForm();
    if (v) return setError(v);

    setLoading(true);
    try {
      await createOrderDoc({
        paymentMethod: "cod",
        status: "pending", // en attente de livraison/paiement à la livraison
      });
      clearCart();
      nav("/orders");
    } catch (e) {
      console.error(e);
      setError("Impossible de créer la commande (paiement à la livraison).");
    } finally {
      setLoading(false);
    }
  };

  const handlePaygate = async () => {
    setError("");
    const v = validateForm();
    if (v) return setError(v);

    setLoading(true);
    try {
      // 1) Créer la commande "en attente de paiement"
      const orderId = await createOrderDoc({
        paymentMethod: "paygate",
        status: "awaiting_payment",
      });

      // 2) Appeler ta Cloud Function pour générer le lien PayGate
      const resp = await fetch(CREATE_PAY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId,
          amount: total,
          customer: { name: form.name, phone: form.phone },
        }),
      });

      if (!resp.ok) {
        const txt = await resp.text().catch(() => "");
        throw new Error(`createPaygatePayment a échoué: ${txt || resp.status}`);
      }

      const { paymentUrl } = await resp.json();
      if (!paymentUrl) throw new Error("Lien de paiement introuvable.");

      // 3) Rediriger l’utilisateur vers PayGate
      // (ne pas clearCart maintenant; attends le webhook pour status=paid)
      window.location.href = paymentUrl;
    } catch (e) {
      console.error(e);
      setError("Impossible de démarrer le paiement mobile. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <h2>Validation de commande</h2>

      <div className="row">
        <div className="col-md-6">
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="mb-3 form-floating">
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className="form-control"
                placeholder="Nom"
                disabled={loading}
              />
              <label>Nom</label>
            </div>

            <div className="mb-3 form-floating">
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="form-control"
                placeholder="Téléphone"
                disabled={loading}
              />
              <label>Téléphone</label>
            </div>

            <div className="mb-3 form-floating">
              <textarea
                name="address"
                value={form.address}
                onChange={handleChange}
                className="form-control"
                placeholder="Adresse"
                style={{ minHeight: 120 }}
                disabled={loading}
              />
              <label>Adresse de livraison</label>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {/* 3 boutons d'action */}
            <div className="d-grid gap-2">
              <button
                type="button"
                className="btn btn-success"
                onClick={handleWhatsApp}
                disabled={loading}
              >
                {loading ? "Traitement…" : "Valider via WhatsApp"}
              </button>

              <button
                type="button"
                className="btn btn-warning"
                onClick={handlePaygate}
                disabled={loading}
              >
                {loading ? "Traitement…" : "Payer par Mobile (PayGate)"}
              </button>

              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCOD}
                disabled={loading}
              >
                {loading ? "Traitement…" : "Paiement à la livraison"}
              </button>
            </div>
          </form>
        </div>

        <div className="col-md-6">
          <h5>Résumé de la commande</h5>
          <ul className="list-group mb-3">
            {cart.map((i) => (
              <li
                key={i.id}
                className="list-group-item d-flex justify-content-between align-items-center"
              >
                <div>
                  <strong>{i.name}</strong>
                  <div className="small text-muted">
                    {i.qty} × {fmt.format(Number(i.price) || 0)}
                  </div>
                </div>
                <div>
                  {fmt.format((Number(i.price) || 0) * (i.qty || 0))}
                </div>
              </li>
            ))}
            <li className="list-group-item d-flex justify-content-between">
              <strong>Total</strong>
              <strong>{fmt.format(total)}</strong>
            </li>
          </ul>

          <p className="text-muted small">
            • <strong>WhatsApp</strong> : envoi de la commande et confirmation
            par chat.<br />
            • <strong>Mobile (PayGate)</strong> : redirection vers la page de
            paiement PayGate.<br />
            • <strong>À la livraison</strong> : paiement à la réception.
          </p>
        </div>
      </div>
    </div>
  );
}
